import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface StockStatus {
  totalProducts: number;
  productsInStock: number;
}

export const useStockStatus = (notificationId: string) => {
  return useQuery({
    queryKey: ['stock-status', notificationId],
    queryFn: async (): Promise<StockStatus> => {
      try {
        // Total monitored products for this notification
        const { data: monitoringRows, error: monitoringError } = await supabase
          .from('notification_product_monitoring')
          .select('id, product_sku, product_bc, product_quantity')
          .eq('notification_id', notificationId)
          .eq('is_monitored', true);

        if (monitoringError) {
          console.error('Error fetching monitoring rows:', monitoringError);
          throw monitoringError;
        }

        const totalProducts = (monitoringRows ?? []).length;

        if (totalProducts === 0) {
          return { totalProducts: 0, productsInStock: 0 };
        }

        // Get SKUs to check in voorraad_live (magazijn 2 only, consistent with edge function)
        const skus = monitoringRows
          .map(m => m.product_sku)
          .filter((sku): sku is string => sku !== null);

        // Fetch current stock from voorraad_live - filtered to magazijn 2
        let stockBySku = new Map<string, number>();

        if (skus.length > 0) {
          const { data: voorraadData } = await supabase
            .from('voorraad_live')
            .select('product_id, voorraad, magazijn')
            .in('product_id', skus)
            .eq('magazijn', '2');
          
          voorraadData?.forEach(item => {
            stockBySku.set(item.product_id, item.voorraad || 0);
          });
        }

        // Count products that have enough stock (voorraad >= required quantity)
        let productsInStock = 0;
        for (const row of monitoringRows) {
          const requiredQty = row.product_quantity || 1;
          let stock = 0;
          if (row.product_sku) {
            stock = stockBySku.get(row.product_sku) || 0;
          }
          if (stock >= requiredQty) {
            productsInStock++;
          }
        }

        return { totalProducts, productsInStock };
      } catch (err) {
        console.error('Error fetching stock status:', err);
        throw err;
      }
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
};
